/**
 * A plan's brief as the plan page shows it (markdown): what will change, object by object; the answers the author
 * gave; what was left to Zoom. And a PDF's text set back into paragraphs for the source fold. Pure.
 */
import type { document, plans } from '@/lib/client';
import { fieldLabel } from '@/lib/plans';

const VERBS: Record<string, string> = { create: 'Create', change: 'Change', remove: 'Remove', use: 'Use' };

/**
 * One line per object the plan changes, in the order its steps run, with what each of its steps does in their own
 * plain titles (titles.ts) under it; a step with no object (a calling plan, one by hand) is its own line. The fields
 * a step sets stay on its panel: a list of them ("uses user \"alexandru\": email, member, …") read as noise.
 */
function changeLines(steps: readonly plans.PlanStepView[]): string[] {
  const groups = new Map<string, { task?: plans.DocumentTask; steps: plans.PlanStepView[] }>();
  for (const step of [...steps].sort((a, b) => a.position - b.position)) {
    const task = step.api?.task;
    const key = task?.key ?? step.key;
    const group = groups.get(key) ?? { task, steps: [] };
    group.steps.push(step);
    groups.set(key, group);
  }
  return [...groups.values()].map(({ task, steps: own }) => {
    if (task === undefined) return `- ${own[0]!.channel === 'human' ? '**By you:** ' : ''}${own[0]!.title}`;
    const head = `- **${VERBS[task.action] ?? 'Change'}** the ${task.kind} **${task.name}**`;
    // The object's own create or change step says no more than the head; the rest say what is set on it.
    const named = (title: string): boolean => title.toLowerCase().includes(task.name.toLowerCase()) && /^(create|make|add|set up|change|update)\b/i.test(title);
    const doing = own.map((step) => step.title).filter((title) => !named(title));
    return doing.length === 0 ? head : `${head}\n${doing.map((title) => `  - ${title}`).join('\n')}`;
  });
}

/** The brief, from the plan and, when it was made from one, its document's answers and assumptions. */
export interface Brief {
  /** What will change and the answers given (markdown). */
  main: string;
  /** What was left to Zoom's defaults, a markdown line each: the page folds them away. */
  defaults: string[];
}

export function planBrief(plan: Pick<plans.PlanView, 'steps'>, doc: document.DocumentView | null): Brief {
  const parts = [`#### What will change\n\n${changeLines(plan.steps).join('\n')}`];
  if (doc === null) return { main: parts[0]!, defaults: [] };
  const asked = new Map(doc.questions.map((q) => [q.id, q.text]));
  const answers = doc.answers.filter((a) => a.text.trim() !== '').map((a) => {
    const question = asked.get(a.answers);
    return question === undefined ? `- ${a.text}` : `- ${question} **${a.text}**`;
  });
  if (answers.length > 0) parts.push(`#### Your answers\n\n${answers.join('\n')}`);
  const answered = new Set(doc.answers.map((a) => a.answers));
  const defaults = doc.assumptions.filter((s) => !answered.has(s.id)).map((s) => (s.about.trim() === '' || s.about === 'object'
    ? `- ${s.text}`
    : `- **${fieldLabel(s.about).replace(/^./, (c) => c.toUpperCase())}**: ${s.text}`));
  return { main: parts.join('\n\n'), defaults };
}

/**
 * A document's text with the PDF's line wraps taken out: a line that does not end a sentence runs on into the next,
 * unless the next starts a list item. A wrapped line runs near the page's width, so a short line is never wrapped: a
 * short one of a few words with no end mark is a heading of its own, and the line after a short one starts afresh.
 */
export function reflow(text: string): string {
  const lines = text.replace(/\r/g, '').split('\n').map((l) => l.trim());
  const out: string[] = [];
  let paragraph = '';
  const flush = () => { if (paragraph !== '') out.push(paragraph); paragraph = ''; };
  lines.forEach((line, i) => {
    if (line === '' || /^--\s*\d+\s+of\s+\d+\s*--$/.test(line)) return flush();
    const next = lines[i + 1] ?? '';
    const heading = line.length < 40 && line.split(/\s+/).length <= 5 && !/[.,;:!?]$/.test(line) && /^[A-Z0-9]/.test(next);
    if (heading) { flush(); out.push(line); return; }
    if (/^([-*•]|\d+[.)])\s/.test(line) || (lines[i - 1] ?? '').length < 60) flush();
    paragraph = paragraph === '' ? line : `${paragraph} ${line}`;
    if (/[.!?:]["')\]]?$/.test(line) && (next === '' || /^[A-Z0-9\-*•]/.test(next))) flush();
  });
  flush();
  return out.join('\n\n');
}
